// === SAA:UTILS:POLICY-COST:v1 ===
// One-time policy/setup fee charged on the first payment for SAA's new-policy flow.
// Source: SAA "kosten" disclosure — €12,50 motor vehicle (with license plate), €6,25 other,
// capped at €12,50 per package, all excluding 21% assurantiebelasting.

import { getProductOrder } from '../workflowConfig';

type ProductSelection = {
  personenauto?: boolean;
  woonhuis?: boolean;
  inboedel?: boolean;
  aansprakelijkheid?: boolean;
  rechtsbijstand?: boolean;
  ongevallen?: boolean;
  doorlopendereis?: boolean;
};

export interface PolicyCostConfig {
  enabled?: boolean;
  carWithPlate?: number;
  carWithoutPlate?: number;
  otherPolicy?: number;
  packageCap?: number;
  taxRate?: number;
}

export interface PolicyCostResult {
  net: number;        // ex-tax one-time fee (floored to 2 decimals)
  tax: number;        // 21% on the net (floored to 2 decimals)
  withTax: number;    // net + tax (the actual amount added to the first debit)
}

const floor2 = (n: number) => Math.floor(n * 100) / 100;

export const computePolicyCost = (
  selectedProducts: ProductSelection | null | undefined,
  config: any,
): PolicyCostResult => {
  const cfg: PolicyCostConfig | undefined = config?.policyCost;
  if (!cfg?.enabled) return { net: 0, tax: 0, withTax: 0 };

  const carWithPlate = cfg.carWithPlate ?? 12.50;
  const otherPolicy = cfg.otherPolicy ?? 6.25;
  const cap = cfg.packageCap ?? 12.50;
  const taxRate = cfg.taxRate ?? 0.21;

  // SAA onboarding only takes out new policies, so personenauto always counts as "with license plate".
  // carWithoutPlate exists for the eventual amendment flow and is intentionally unused here.
  let sum = 0;
  if (selectedProducts?.personenauto) sum += carWithPlate;
  if (selectedProducts?.woonhuis) sum += otherPolicy;
  if (selectedProducts?.inboedel) sum += otherPolicy;
  if (selectedProducts?.aansprakelijkheid) sum += otherPolicy;
  if (selectedProducts?.rechtsbijstand) sum += otherPolicy;
  if (selectedProducts?.ongevallen) sum += otherPolicy;
  if (selectedProducts?.doorlopendereis) sum += otherPolicy;

  const net = Math.min(sum, cap);
  const tax = floor2(net * taxRate);
  return { net: floor2(net), tax, withTax: floor2(net + net * taxRate) };
};

export type ProductKey = 'personenauto' | 'woonhuis' | 'inboedel' | 'aansprakelijkheid' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis';

// Cap-aware allocation: hand out each selected product's per-policy contribution
// until the package cap is reached, then return 0 for the rest. Priority follows the
// configured product order (getProductOrder) so UI, document and allocation agree.
// With the default order the Wonen & Gezin products come first (6,25 each), so personenauto
// only receives what is left of the 12,50 cap; alone it satisfies the cap by itself.

// Identify which product a contract is from its PP shape — matches the detection used in
// emailTemplate / pricing helpers. Returns null for unknown shapes (e.g. package mantel rows).
export const identifyProduct = (pp: any): ProductKey | null => {
  if (!pp) return null;
  if (pp.OB) return 'personenauto';
  if (pp.IP) return 'inboedel';
  if (pp.OP && !pp.IP && !pp.DA) return 'woonhuis';
  if (pp.DA && !pp.IP && !pp.OP && !pp.OB) return 'aansprakelijkheid';
  if (pp.WC && !pp.OB && !pp.IP && !pp.OP && !pp.DA) return 'rechtsbijstand';
  if (pp.OD && !pp.DR) return 'ongevallen';
  if (pp.DR || pp.OD) return 'doorlopendereis';
  return null;
};

// Walk a list of contract products and set PP_EKST per product from the allocation.
// Products without a recognised PP shape are left untouched.
export const applyPolicyCostAllocationToProducts = (
  products: any[] | null | undefined,
  allocation: Record<ProductKey, number>,
): void => {
  if (!products) return;
  for (const product of products) {
    const key = identifyProduct(product?.PP);
    if (!key) continue;
    product.PP.PP_EKST = allocation[key] ?? 0;
  }
};

export const computePolicyCostAllocation = (
  selectedProducts: ProductSelection | null | undefined,
  config: any,
): Record<ProductKey, number> => {
  const result: Record<ProductKey, number> = {
    personenauto: 0, woonhuis: 0, inboedel: 0, aansprakelijkheid: 0, rechtsbijstand: 0, ongevallen: 0, doorlopendereis: 0,
  };
  const cfg: PolicyCostConfig | undefined = config?.policyCost;
  if (!cfg?.enabled || !selectedProducts) return result;

  const carWithPlate = cfg.carWithPlate ?? 12.50;
  const otherPolicy = cfg.otherPolicy ?? 6.25;
  const cap = cfg.packageCap ?? 12.50;

  let remaining = cap;
  for (const key of getProductOrder() as ProductKey[]) {
    if (!selectedProducts[key] || remaining <= 0) continue;
    const candidate = key === 'personenauto' ? carWithPlate : otherPolicy;
    const allocated = Math.min(candidate, remaining);
    result[key] = floor2(allocated);
    remaining = floor2(remaining - allocated);
  }
  return result;
};

